import React, { useState, useEffect, useMemo } from 'react';
import { subscribeToDocuments } from '@coparent/shared/src/services/documentService';
import { DOCUMENT_CATEGORIES } from '@coparent/shared/src/utils/constants';
import { HiX, HiDocumentText, HiPlusCircle } from 'react-icons/hi';

/**
 * Modal for choosing an existing file from the Documents vault. Reused by
 * activities (and available to messages). Calls onSelect(document).
 */
const DocumentPickerModal = ({ familyId, onSelect, onClose }) => {
  const [documents, setDocuments] = useState([]);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    if (!familyId) return undefined;
    return subscribeToDocuments(familyId, setDocuments);
  }, [familyId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return documents.filter((doc) => {
      if (categoryFilter !== 'all' && doc.category !== categoryFilter) return false;
      if (q && !(doc.name || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [documents, search, categoryFilter]);

  const categoryLabel = (value) =>
    DOCUMENT_CATEGORIES.find((c) => c.value === value)?.label || value;

  return (
    <div className="modal-overlay">
      <div className="modal-content max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-ink-900">Choose from Documents</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-ink-400 hover:bg-surface-100 hover:text-ink-600 transition-colors">
            <HiX className="text-lg" />
          </button>
        </div>

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search documents…"
          className="input-base mb-3"
        />

        <div className="flex gap-1.5 flex-wrap mb-3">
          {[{ value: 'all', label: 'All' }, ...DOCUMENT_CATEGORIES].map((cat) => (
            <button
              key={cat.value}
              type="button"
              onClick={() => setCategoryFilter(cat.value)}
              className={`px-3 py-1 text-xs font-bold rounded-full transition-colors ${
                categoryFilter === cat.value ? 'bg-ink-800 text-white' : 'bg-surface-100 text-ink-500 hover:bg-surface-200'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto space-y-2">
          {filtered.length === 0 ? (
            <p className="text-sm text-ink-300 text-center py-8">No documents found.</p>
          ) : (
            filtered.map((doc) => (
              <button
                key={doc.id}
                type="button"
                onClick={() => { onSelect(doc); onClose(); }}
                className="w-full flex items-center gap-3 p-3 rounded-xl border border-surface-200 hover:bg-surface-50 transition-colors text-left"
              >
                <HiDocumentText className="text-ink-400 flex-shrink-0 text-lg" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-ink-800 truncate">{doc.name}</p>
                  <p className="text-xs text-ink-400">{categoryLabel(doc.category)}</p>
                </div>
                <HiPlusCircle className="text-accent-500 flex-shrink-0 text-lg" />
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentPickerModal;
